import { prepareEnrollmentSignatureRequest } from '@/api/enrollment-api'
import { ENROLLMENT_COMMON_MESSAGES } from '../../../../constants/enrollmentContent'
import type { HydrateEnrollmentSessionFn } from '../../types'
import { SELF_ENROLLMENT_STEPS } from '../../utils/steps'
import {
  getRequestErrorMessage,
  getRequestStatus,
  getSignatureStatus,
  getSigningUrl,
  hasSignedEnrollmentAgreement,
  isStartDateMondayError
} from '../../utils/utils'
import { recoverExistingSigningSession } from './recovery'

type OpenDocusignSigningSessionArgs = {
  enrollmentSessionToken: string
  hydrateEnrollmentSession: HydrateEnrollmentSessionFn
  goToStep: (step: number) => void
  setStepSubmissionError: (value: string | null) => void
  setSignatureStatus: (value: string | null) => void
  setPreparingDocusignState: () => void
  isRestartingDocusignRef: { current: boolean }
  completeSigningFlow: () => Promise<void>
  openDocusignModalWithUrl: (signingUrl: string) => void
}

export async function openDocusignSigningSession({
  enrollmentSessionToken,
  hydrateEnrollmentSession,
  goToStep,
  setStepSubmissionError,
  setSignatureStatus,
  setPreparingDocusignState,
  isRestartingDocusignRef,
  completeSigningFlow,
  openDocusignModalWithUrl
}: OpenDocusignSigningSessionArgs) {
  try {
    const response = await prepareEnrollmentSignatureRequest(enrollmentSessionToken)
    const status = getSignatureStatus(response)
    const signed = hasSignedEnrollmentAgreement(response)

    setSignatureStatus(status)
    isRestartingDocusignRef.current = false

    if (signed) {
      await completeSigningFlow()
      return
    }

    const signingUrl = getSigningUrl(response)

    if (!signingUrl) {
      setStepSubmissionError(ENROLLMENT_COMMON_MESSAGES.enrollmentAgreementOpenFailed)
      return
    }

    openDocusignModalWithUrl(signingUrl)
  } catch (error) {
    isRestartingDocusignRef.current = false
    setPreparingDocusignState()

    if (getRequestStatus(error) === 409) {
      try {
        const recovered = await recoverExistingSigningSession({
          hydrateEnrollmentSession,
          setSignatureStatus,
          completeSigningFlow,
          openDocusignModalWithUrl
        })

        if (recovered) {
          return
        }
      } catch (stateError) {
        console.warn('Failed to refresh enrollment signature state', stateError)
      }
    }

    const message = getRequestErrorMessage(
      error,
      ENROLLMENT_COMMON_MESSAGES.enrollmentAgreementPrepareFailed
    )

    if (isStartDateMondayError(message)) {
      setStepSubmissionError(ENROLLMENT_COMMON_MESSAGES.startDateMustBeMonday)
      goToStep(SELF_ENROLLMENT_STEPS.TRAINING_DATE)
      return
    }

    setStepSubmissionError(message)
  }
}
